import {
  useEffect, useState
} from 'react'
import {
  useLocation,
  useNavigate
} from 'react-router-dom'
import {
  useAuth
} from '../../hooks/useAuth'
import {
  clearPendingRoute,
  peekPendingRoute,
} from '../../utils/pendingRoute'
import {
  useOnboardingState
} from '../../hooks/useOnboardingState'
import type {
  ConnectedChannel
} from '../../hooks/useOnboardingState'
import type {
  AssistantCharacter
} from '../../services/api'
import {
  authService
} from '../../services/api'
import {
  StepIndicator
} from './StepIndicator'
import {
  PermissionsStep
} from './PermissionsStep'
import {
  CharacterSelectionStep
} from './CharacterSelectionStep'
import {
  ChannelConnectionStep
} from './ChannelConnectionStep'
import {
  CompletionStep
} from './CompletionStep'
import {
  BillingStep
} from './BillingStep'
import {
  ToolsStep
} from './ToolsStep'
import {
  OnboardingWizardEvents,
  trackEvent
} from '../../analytics/posthog'
import {
  hasScopeCluster,
  missingScopeClusters,
  parseScopeClusters
} from '../../utils/scopeClusters'
import {
  Loader2
} from 'lucide-react'

// The channel connect comes before the mail/calendar scopes ask (the concluded
// onboarding-channel-first experiment, GET-8885).
const GOOGLE_LABELS = ['Choose', 'Connect', 'Permissions', 'Billing', 'Tools', 'Ready!']
const MICROSOFT_LABELS = ['Choose', 'Connect', 'Billing', 'Tools', 'Ready!']

const CatchLogo = () => (
  <img src="/images/logo/symbol.svg" alt="Catch" className="h-7 w-auto" />
)

interface ScopeUpgradeNavState {
  scopeUpgrade?: boolean
  requestedClusters?: string | null
}

export function OnboardingPage(): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const onboardingState = useOnboardingState()
  const navState = location.state as ScopeUpgradeNavState | null

  // null until the hook's derived step has been applied. Defaulting to 1 made
  // every mount render step 1 for a frame before the effect below corrected it
  // — that flashed the character picker at returning
  // users and fired a spurious onboarding_character_step_viewed on each mount,
  // inflating that step's funnel (one production user logged six of them with a
  // single character_selected).
  const [currentStep, setCurrentStep] = useState<number | null>(null)
  const [selectedCharacter, setSelectedCharacter] = useState<AssistantCharacter | null>(null)
  const [connectedChannel, setConnectedChannel] = useState<ConnectedChannel>(null)
  const [wizardStarted, setWizardStarted] = useState(false)

  const isGoogleUser = onboardingState.isGoogleUser
  const labels = isGoogleUser ? GOOGLE_LABELS : MICROSOFT_LABELS

  // Step mapping for Google users:
  //   1 = Character, 2 = Channel, 3 = Permissions, 4 = Billing, 5 = Tools, 6 = Completion
  // Step mapping for Microsoft users (no permissions step):
  //   1 = Character, 2 = Channel, 3 = Billing, 4 = Tools, 5 = Completion
  // Tools is skipped (routed straight to Completion) when the user has no
  // mail/calendar grant — see showToolsStep below.
  const characterStep = 1
  const channelStep = 2
  const permissionsStep = 3
  const billingStep = isGoogleUser ? 4 : 3
  // Inbox-scan integration suggestions. Sits after the card because it is the
  // one step that is purely upside — and it skips itself when the scan found
  // nothing, so it never becomes a blank screen between billing and the finish.
  const toolsStep = isGoogleUser ? 5 : 4
  const completionStep = isGoogleUser ? 6 : 5
  // A user who declined mail/calendar was never scanned, so the step would sit
  // on its poll budget and then skip — ten seconds of dead wizard. Worse, while
  // it waits it tells them "We're reading who has emailed you", which for them
  // is simply untrue. Route past it entirely.
  const showToolsStep = onboardingState.hasMailCalendarScopes
  const afterBillingStep = showToolsStep ? toolsStep : completionStep

  // The hook's derivation is a *resume point*, not a live opinion about the
  // current step, so it is applied exactly once — `currentStep === null` is
  // already the "not yet initialized" flag and nothing ever sets it back.
  //
  // This used to re-fire on every change to the derived step, which let a
  // refetch of the onboarding state overwrite where the user had navigated to
  // in-session: completing billing flips needsBillingStep false, the hook
  // re-derives the next step, and the one the wizard had just moved to was
  // replaced before it could render.
  useEffect(() => {
    if (onboardingState.isLoading) return
    if (!onboardingState.needsOnboarding) {
      // Where a deep link was going before SetupGuard sent the user here.
      // Without it every link we hand a half-onboarded user ends on this page's
      // default hand-off instead, with no trace of what it asked for
      // (GET-13956).
      const pending = peekPendingRoute()
      clearPendingRoute()
      navigate(pending ?? '/integrations', { replace: true })
      return
    }
    if (currentStep !== null) return
    setCurrentStep(onboardingState.currentStep)
  }, [
    onboardingState.isLoading,
    onboardingState.needsOnboarding,
    onboardingState.currentStep,
    currentStep,
  ])

  // Kept out of the initializer above: the server's view of the connected
  // channel is only ever extra information, never a step decision, so picking
  // it up on a refetch is safe.
  useEffect(() => {
    if (onboardingState.connectedChannel) {
      setConnectedChannel(onboardingState.connectedChannel)
    }
  }, [onboardingState.connectedChannel])

  // selectedCharacter is only populated by an in-session pick, so a user who
  // came back to finish onboarding reached the channel, billing and completion
  // steps with no name to show and silently fell back to generic copy, which
  // loses the billing step's personalization. Hydrate from the persisted selection instead.
  // Guarded on selected_character_id: /me/assistant answers with a *default*
  // character when nothing is chosen, and naming an assistant the user never
  // picked is worse than the generic fallback.
  useEffect(() => {
    if (selectedCharacter != null || user?.selected_character_id == null) return
    let cancelled = false
    void authService.getAssistantCharacter()
      .then((character) => {
        if (!cancelled) setSelectedCharacter(character)
      })
      // Non-fatal: the copy degrades to the same generic fallback as before.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [user?.selected_character_id, selectedCharacter])

  // Track wizard start once
  useEffect(() => {
    if (!onboardingState.isLoading && onboardingState.needsOnboarding && !wizardStarted) {
      trackEvent(OnboardingWizardEvents.WIZARD_STARTED, {
        initial_step: onboardingState.currentStep,
        is_google_user: isGoogleUser,
      })
      setWizardStarted(true)
    }
  }, [onboardingState.isLoading, onboardingState.needsOnboarding, wizardStarted])

  // Where to land once both channel and permissions are behind the user.
  // Falls through to the tools step, not completion: a user whose card is
  // already on file (a team member, or an active cardless trial) still gets the
  // suggestions — otherwise the whole step is invisible to exactly the cohort
  // that reached the product fastest.
  const postGatesStep = onboardingState.needsBillingStep
    ? billingStep
    : afterBillingStep
  // Google users still owing the scopes grant go to permissions next;
  // everyone else falls through to billing/completion. Keyed on
  // permissionsStepBlocking, not the raw scope status, so a user who chose to
  // set Google up later is not sent back to it after connecting a channel —
  // the exact bounce that trapped users in production.
  const stepAfterChannel = isGoogleUser && onboardingState.permissionsStepBlocking
    ? permissionsStep
    : postGatesStep
  // Permissions is the last gate before billing/completion.
  const stepAfterPermissions = postGatesStep

  // Reachable only from the step's "Permissions granted" confirmation screen,
  // which renders when the grant is already held — and the wizard routes here
  // only while `permissionsStepBlocking` is true, i.e. while it is not. So this
  // fires for the rare user who arrives at the step already granted (2 events
  // in 90 days) and never for the one who just granted: that redirect unmounts
  // the whole wizard, and CallbackPage emits PERMISSIONS_GRANTED on the way
  // back instead. The two cannot both fire for one grant, which is why this
  // call stays rather than moving wholesale.
  const handlePermissionsContinue = (): void => {
    trackEvent(OnboardingWizardEvents.PERMISSIONS_GRANTED)
    setCurrentStep(stepAfterPermissions)
  }

  const handlePermissionsDeferred = (): void => {
    trackEvent(OnboardingWizardEvents.PERMISSIONS_DEFERRED, {
      // Whether they had already connected a channel — the cohort that
      // previously got bounced back here after doing the harder thing.
      has_connected_channel: onboardingState.channelStepComplete,
    })
    onboardingState.deferPermissions()
    setCurrentStep(stepAfterPermissions)
  }

  const handleCharacterSelected = (character: AssistantCharacter): void => {
    setSelectedCharacter(character)
    if (onboardingState.channelStepComplete) {
      setCurrentStep(stepAfterChannel)
    } else {
      setCurrentStep(channelStep)
    }
  }

  const handleChannelConnected = async (channel: ConnectedChannel): Promise<void> => {
    setConnectedChannel(channel)
    // Advance BEFORE refreshing the statuses, and never behind the await: where
    // to go next is decided by the scopes/billing gates, which the channel
    // connect cannot have changed, so the refetch buys this decision nothing —
    // it only supplies a way for the step to never happen. A user who connected
    // a channel and was left staring at the same grid re-taps Connect, and the
    // second attempt on an already-linked handle is what they read as failure.
    setCurrentStep(stepAfterChannel)
    try {
      await onboardingState.refetchChannelStatus()
    } catch {
      // Non-fatal: the next step renders from state the connect did not touch,
      // and the following mount re-derives everything from the server anyway.
    }
  }

  const handleBillingCompleted = (): void => {
    setCurrentStep(afterBillingStep)
  }

  // Detect when user returned from a scope upgrade without granting mail_calendar
  const scopeUpgradeFailed = !!(
    navState?.scopeUpgrade &&
    !onboardingState.isLoading &&
    (onboardingState.scopeStatus === null ||
      !hasScopeCluster(onboardingState.scopeStatus, 'mail_calendar'))
  )
  // Which clusters to name in the retry, so it points at the box the user left
  // unchecked instead of restarting the generic step. The return trip knows
  // what was asked for; the server's per-cluster gap covers every other mount,
  // including a partial grant made days ago — which is when the user this was
  // written for actually arrives, since the step now holds them (GET-11570).
  const incompleteClusters = onboardingState.scopeStatus === null
    ? []
    : navState?.scopeUpgrade
      ? missingScopeClusters(
        onboardingState.scopeStatus,
        parseScopeClusters(navState.requestedClusters),
      )
      : Object.keys(onboardingState.scopeStatus.incomplete_clusters ?? {})

  // Hold the loader until the derived step is known, so no step ever renders
  // (or fires its viewed event) only to be replaced on the next tick.
  if (onboardingState.isLoading || currentStep === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        <CatchLogo />
        <button
          onClick={() => { void logout() }}
          className="text-gray-500 hover:text-primary-600 text-sm font-medium transition-colors duration-200"
        >
          Logout
        </button>
      </div>

      {/* Main content */}
      <div className="flex items-center justify-center px-4 pb-8" style={{ minHeight: 'calc(100vh - 72px)' }}>
        <div className="w-full max-w-xl">
          {/* Glass card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-soft-lg p-6 sm:p-10">
            <div className="mb-8">
              <StepIndicator
                currentStep={currentStep}
                labels={labels}
                skippedSteps={[
                  ...(isGoogleUser
                  && onboardingState.permissionsDeferred
                  && !onboardingState.hasMailCalendarScopes
                    ? [permissionsStep]
                    : []),
                  // Without this the indicator shows a Tools step the user is
                  // routed straight past, and the number jumps 4 -> 6. Marking
                  // it skipped keeps the numbering (which useOnboardingState
                  // also derives, so it must not shift) while showing a muted
                  // dash instead of a step they never reach.
                  ...(showToolsStep ? [] : [toolsStep]),
                ]}
              />
            </div>

            {isGoogleUser && currentStep === permissionsStep && (
              <PermissionsStep
                scopeStatus={onboardingState.scopeStatus}
                onContinue={handlePermissionsContinue}
                onDefer={handlePermissionsDeferred}
                incompleteClusters={incompleteClusters}
                scopeUpgradeFailed={scopeUpgradeFailed}
                assistantName={selectedCharacter?.name}
              />
            )}
            {currentStep === characterStep && (
              <CharacterSelectionStep
                onSelect={handleCharacterSelected}
                initialSelectedId={user?.selected_character_id}
              />
            )}
            {currentStep === channelStep && (
              <ChannelConnectionStep
                assistantName={selectedCharacter?.name}
                userId={user?.id}
                smsFeatureEnabled={onboardingState.smsFeatureEnabled}
                onConnected={(channel) => void handleChannelConnected(channel)}
              />
            )}
            {currentStep === billingStep && (
              <BillingStep
                onCompleted={handleBillingCompleted}
                teamInvite={onboardingState.teamInvite}
                characterName={selectedCharacter?.name}
                connectedChannel={connectedChannel || onboardingState.connectedChannel}
                hasMailCalendarScopes={onboardingState.hasMailCalendarScopes}
              />
            )}
            {currentStep === toolsStep && showToolsStep && (
              <ToolsStep onContinue={() => setCurrentStep(completionStep)} />
            )}
            {currentStep === completionStep && (
              <CompletionStep
                character={selectedCharacter}
                connectedChannel={connectedChannel || onboardingState.connectedChannel}
                whatsappLink={onboardingState.whatsappStatus?.phone_number}
                slackDeepLink={onboardingState.slackStatus?.deep_link_url}
                slackWorkspaceName={onboardingState.slackStatus?.workspace_name}
                smsPhoneNumber={onboardingState.smsStatus?.phone_number}
                imessageFromNumber={onboardingState.imessageStatus?.linq_from_number}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
